/**
 * Orders Redux Slice
 * State + reducers for orders domain
 * Uses: domain/types
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { Order, OrderStatus, OrderStats, OrdersFilter, OrdersSortBy, OrdersSortOrder, CreateOrderRequest, PaginationMeta, PaginatedOrders } from '../domain/types'
import { DEFAULT_PAGE_SIZE } from '../domain/constants'
import { DEFAULT_ORDERS_FILTER } from '../domain/filter-config'

export interface VariantFilterOption {
  value: string
  en: string
  ar?: string
}

export interface OrdersState {
  items: Order[]
  selectedOrder: Order | null
  stats: OrderStats
  filter: OrdersFilter
  pagination: PaginationMeta
  loading: boolean
  error: string | null
  variantFilterOptions: VariantFilterOption[]
  // Multi-select state for bulk actions
  selectedOrderIds: string[]
  isAllPagesSelected: boolean
  bulkDownloading: boolean
  bulkCancelling: boolean
  lastBulkCancelResult: {
    successCount: number
    failureCount: number
  } | null
  // Force status update state (super_admin only)
  bulkForceUpdating: boolean
  lastBulkForceUpdateResult: {
    successCount: number
    failureCount: number
  } | null
}

const initialState: OrdersState = {
  items: [],
  selectedOrder: null,
  stats: {
    pending: 0,
    processing: 0,
    delivered: 0,
    total: 0,
  },
  filter: { ...DEFAULT_ORDERS_FILTER },
  pagination: {
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    pageCount: 1,
    total: 0,
  },
  loading: false,
  error: null,
  variantFilterOptions: [],
  // Multi-select initial state
  selectedOrderIds: [],
  isAllPagesSelected: false,
  bulkDownloading: false,
  bulkCancelling: false,
  lastBulkCancelResult: null,
  // Force status update initial state
  bulkForceUpdating: false,
  lastBulkForceUpdateResult: null,
}

export const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    // Data setters (used by loaders)
    setOrders: (state, action: PayloadAction<Order[]>) => {
      state.items = action.payload
      state.loading = false
      state.error = null
    },
    setSelectedOrder: (state, action: PayloadAction<Order | null>) => {
      state.selectedOrder = action.payload
    },
    setStats: (state, action: PayloadAction<OrderStats>) => {
      state.stats = action.payload
    },
    setFilter: (state, action: PayloadAction<OrdersFilter>) => {
      state.filter = action.payload
      // Keep pagination.page in sync so PaginationControls reflects the correct
      // current page immediately (before the API response arrives).
      if (action.payload.page !== undefined) {
        state.pagination.page = action.payload.page
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.loading = false
    },

    // Action triggers (handled by sagas)
    fetchOrders: (state, _action: PayloadAction<OrdersFilter | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchOrdersSuccess: (state, action: PayloadAction<PaginatedOrders>) => {
      state.items = action.payload.data
      state.pagination = action.payload.pagination
      state.loading = false
    },
    fetchOrdersFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Pagination actions
    setPage: (state, action: PayloadAction<number>) => {
      state.filter.page = action.payload
      state.pagination.page = action.payload // Update pagination immediately
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filter.pageSize = action.payload
      state.filter.page = 1 // Reset to first page when page size changes
      state.pagination.pageSize = action.payload // Update pagination immediately
      state.pagination.page = 1
    },
    goToNextPage: (state) => {
      if (state.pagination.page < state.pagination.pageCount) {
        state.filter.page = (state.filter.page ?? 1) + 1
      }
    },
    goToPreviousPage: (state) => {
      if ((state.filter.page ?? 1) > 1) {
        state.filter.page = (state.filter.page ?? 1) - 1
      }
    },

    createOrder: (state, _action: PayloadAction<CreateOrderRequest>) => {
      state.loading = true
      state.error = null
    },
    createOrderSuccess: (state, action: PayloadAction<Order>) => {
      state.items.unshift(action.payload)
      state.stats.pending += 1
      state.stats.total += 1
      state.loading = false
    },
    createOrderFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    cancelOrder: (state, _action: PayloadAction<{ orderId: string; reason: string }>) => {
      state.error = null
    },
    cancelOrderSuccess: (state, action: PayloadAction<Order>) => {
      const index = state.items.findIndex(o => o.id === action.payload.id)
      if (index !== -1) {
        if (state.selectedOrder?.id === action.payload.id) {
          state.selectedOrder = action.payload
        }
        const oldStatus = state.items[index].status
        state.items[index] = action.payload

        // Update stats
        if (oldStatus === 'pending') state.stats.pending -= 1
        if (oldStatus === 'processing') state.stats.processing -= 1
      }
      state.loading = false
    },
    cancelOrderFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Stats actions
    fetchOrderStats: () => {
      // Don't set loading for stats (background operation)
    },
    fetchOrderStatsSuccess: (state, action: PayloadAction<OrderStats>) => {
      state.stats = action.payload
    },
    fetchOrderStatsFailure: () => {
      // Silent fail for stats
    },

    // Search/filter
    searchOrders: (state, action: PayloadAction<string>) => {
      state.filter = { ...state.filter, search: action.payload, page: 1 }
    },
    filterByStatus: (state, action: PayloadAction<OrderStatus[]>) => {
      state.filter = { ...state.filter, status: action.payload, page: 1 }
    },
    setSortBy: (state, action: PayloadAction<OrdersSortBy>) => {
      state.filter = { ...state.filter, sortBy: action.payload, page: 1 }
    },
    setSortOrder: (state, action: PayloadAction<OrdersSortOrder>) => {
      state.filter = { ...state.filter, sortOrder: action.payload, page: 1 }
    },
    setSort: (state, action: PayloadAction<{ sortBy: OrdersSortBy; sortOrder: OrdersSortOrder }>) => {
      state.filter = { ...state.filter, sortBy: action.payload.sortBy, sortOrder: action.payload.sortOrder, page: 1 }
    },
    setDateRange: (state, action: PayloadAction<{ dateFrom?: string; dateTo?: string }>) => {
      state.filter = { ...state.filter, dateFrom: action.payload.dateFrom, dateTo: action.payload.dateTo, page: 1 }
    },
    setSalesOrderFilter: (state, action: PayloadAction<string | undefined>) => {
      state.filter = { ...state.filter, salesOrderId: action.payload }
    },

    // Clear state (also sets loading=true so spinner shows immediately on language change)
    clearOrders: (state) => {
      state.items = []
      state.selectedOrder = null
      state.error = null
      state.loading = true
    },
    clearError: (state) => {
      state.error = null
    },

    // Variant filter options (for filter drawer — all variants, not just current page)
    fetchVariantOptions: () => {
      // trigger saga
    },
    setVariantFilterOptions: (state, action: PayloadAction<VariantFilterOption[]>) => {
      state.variantFilterOptions = action.payload
    },

    // Multi-select actions for bulk operations
    toggleOrderSelection: (state, action: PayloadAction<string>) => {
      const orderId = action.payload
      const index = state.selectedOrderIds.indexOf(orderId)
      if (index === -1) {
        state.selectedOrderIds.push(orderId)
      } else {
        state.selectedOrderIds.splice(index, 1)
      }
      // Clear isAllPagesSelected when manually toggling
      state.isAllPagesSelected = false
    },
    selectAllOnPage: (state, action: PayloadAction<string[]>) => {
      const pageOrderIds = action.payload
      // Add all page order IDs that aren't already selected
      pageOrderIds.forEach(id => {
        if (!state.selectedOrderIds.includes(id)) {
          state.selectedOrderIds.push(id)
        }
      })
      state.isAllPagesSelected = false
    },
    deselectAllOnPage: (state, action: PayloadAction<string[]>) => {
      const pageOrderIds = action.payload
      state.selectedOrderIds = state.selectedOrderIds.filter(id => !pageOrderIds.includes(id))
      state.isAllPagesSelected = false
    },
    selectAllAcrossPages: (state) => {
      // When selecting all across pages, we set a flag
      // The actual order IDs will be fetched when download is triggered
      state.isAllPagesSelected = true
      // Also select all currently visible pending orders
      state.items.forEach(order => {
        if (order.status === 'pending' && !state.selectedOrderIds.includes(order.id)) {
          state.selectedOrderIds.push(order.id)
        }
      })
    },
    setSelectedOrderIds: (state, action: PayloadAction<string[]>) => {
      state.selectedOrderIds = action.payload
      state.isAllPagesSelected = false
    },
    clearSelection: (state) => {
      state.selectedOrderIds = []
      state.isAllPagesSelected = false
    },
    setBulkDownloading: (state, action: PayloadAction<boolean>) => {
      state.bulkDownloading = action.payload
    },
    bulkCancelOrders: (state, _action: PayloadAction<{ orderIds: string[]; reason: string }>) => {
      state.bulkCancelling = true
      state.error = null
      state.lastBulkCancelResult = null
    },
    bulkCancelOrdersSuccess: (
      state,
      action: PayloadAction<{
        successCount: number
        failureCount: number
        succeededOrderIds: string[]
      }>
    ) => {
      const succeededIds = new Set(action.payload.succeededOrderIds)

      state.items = state.items.map((order) => {
        if (!succeededIds.has(order.id)) return order
        if (order.status !== 'pending') return order
        return { ...order, status: 'cancelled' as const }
      })

      if (state.selectedOrder && succeededIds.has(state.selectedOrder.id)) {
        state.selectedOrder = { ...state.selectedOrder, status: 'cancelled' }
      }

      state.selectedOrderIds = state.selectedOrderIds.filter((id) => !succeededIds.has(id))
      state.isAllPagesSelected = false
      state.bulkCancelling = false
      state.lastBulkCancelResult = {
        successCount: action.payload.successCount,
        failureCount: action.payload.failureCount,
      }
    },
    bulkCancelOrdersFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.bulkCancelling = false
    },
    clearBulkCancelResult: (state) => {
      state.lastBulkCancelResult = null
    },

    // Force status update actions (super_admin only)
    bulkForceUpdateStatus: (
      state,
      _action: PayloadAction<{
        orderIds: string[]
        newStatus: OrderStatus
        reason: string
        incidentRef?: string
        affectedOpsNote?: string
        forceJobId: string
      }>
    ) => {
      state.bulkForceUpdating = true
      state.error = null
      state.lastBulkForceUpdateResult = null
    },
    bulkForceUpdateStatusSuccess: (
      state,
      action: PayloadAction<{
        successCount: number
        failureCount: number
        succeededOrderIds: string[]
        newStatus: OrderStatus
      }>
    ) => {
      const succeededIds = new Set(action.payload.succeededOrderIds)
      state.items = state.items.map((order) => {
        if (!succeededIds.has(order.id)) return order
        return { ...order, status: action.payload.newStatus }
      })
      if (state.selectedOrder && succeededIds.has(state.selectedOrder.id)) {
        state.selectedOrder = { ...state.selectedOrder, status: action.payload.newStatus }
      }
      state.selectedOrderIds = state.selectedOrderIds.filter((id) => !succeededIds.has(id))
      state.isAllPagesSelected = false
      state.bulkForceUpdating = false
      state.lastBulkForceUpdateResult = {
        successCount: action.payload.successCount,
        failureCount: action.payload.failureCount,
      }
    },
    bulkForceUpdateStatusFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.bulkForceUpdating = false
    },
    clearBulkForceUpdateResult: (state) => {
      state.lastBulkForceUpdateResult = null
    },

    // Select all on page regardless of status (super_admin bulk-select)
    selectAllAnyStatus: (state, action: PayloadAction<string[]>) => {
      const pageOrderIds = action.payload
      pageOrderIds.forEach((id) => {
        if (!state.selectedOrderIds.includes(id)) {
          state.selectedOrderIds.push(id)
        }
      })
      state.isAllPagesSelected = false
    },

    // Update driver/vehicle info on an order after a successful resource assignment
    updateOrderDriverInfo: (
      state,
      action: PayloadAction<{ orderId: string; driverName?: string; vehicleLicensePlate?: string }>
    ) => {
      const { orderId, driverName, vehicleLicensePlate } = action.payload
      const index = state.items.findIndex(o => o.id === orderId)
      if (index !== -1) {
        state.items[index] = { ...state.items[index], driverName, vehicleLicensePlate }
      }
      if (state.selectedOrder?.id === orderId) {
        state.selectedOrder = { ...state.selectedOrder, driverName, vehicleLicensePlate }
      }
    },

    // Replace a single order in the list (e.g., after a resource update re-fetch)
    updateOrderInList: (state, action: PayloadAction<Order>) => {
      const index = state.items.findIndex(o => o.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
      if (state.selectedOrder?.id === action.payload.id) {
        state.selectedOrder = action.payload
      }
    },
  },
})

export const ordersActions = ordersSlice.actions
export const ordersReducer = ordersSlice.reducer
